<template>
  <section
    v-if="cities.length"
    class="service-area-module"
    aria-labelledby="service-area-title"
  >
    <div class="service-area-inner">
      <div class="service-area-map" aria-label="Service area map">
        <ClientOnly>
          <GoogleMap
            v-if="center && googleApiKey"
            :center="center"
            :zoom="10"
            :api-key="googleApiKey"
            :map-type-control="false"
            :street-view-control="false"
            :rotate-control="false"
            class="service-area-google-map"
            map-type-id="roadmap"
            map-id="DEMO_MAP_ID"
          >
            <AdvancedMarker :options="markerOptions" />
          </GoogleMap>
          <div
            v-else
            class="service-area-map-placeholder"
            aria-hidden="true"
          />
          <template #fallback>
            <div class="service-area-map-placeholder" aria-hidden="true" />
          </template>
        </ClientOnly>
      </div>

      <div class="service-area-content">
        <h2 id="service-area-title">
          Areas We Service Near {{ location }}.
        </h2>
        <p>
          From {{ location }} to neighboring communities, Spring-Green provides
          reliable lawn care services tailored to local conditions. Whether you
          are looking for greener grass, healthier trees and shrubs, or
          effective pest control, our team is here to help your
          property look its best.
        </p>

        <ul class="service-area-city-list" aria-label="Cities we service">
          <li v-for="city in cities" :key="`${city.name}-${city.state}`">
            <NuxtLink v-if="city.href" :to="city.href">
              {{ city.name }}<span v-if="city.state">, {{ city.state }}</span>
            </NuxtLink>
            <span v-else>
              {{ city.name }}<span v-if="city.state">, {{ city.state }}</span>
            </span>
          </li>
        </ul>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { AdvancedMarker, GoogleMap } from "vue3-google-map";

interface ServiceAreaCity {
  name: string;
  state?: string;
  href?: string;
}

interface MapCenter {
  lat: number;
  lng: number;
}

const props = defineProps<{
  cities: ServiceAreaCity[];
  location: string;
  center: MapCenter | null;
  googleApiKey?: string;
}>();

const markerOptions = computed(() =>
  props.center ? { position: props.center } : undefined
);
</script>

<style scoped>
.service-area-module {
  background: #eef1f4;
  padding: 4.5rem 2rem;
}

.service-area-inner {
  align-items: center;
  display: grid;
  gap: 4rem;
  grid-template-columns: minmax(0, 1.15fr) minmax(0, 0.85fr);
  margin: 0 auto;
  max-width: 1180px;
}

.service-area-map {
  aspect-ratio: 1.45;
  background: #c9f2df;
  border-radius: 18px;
  min-height: 280px;
  overflow: hidden;
  position: relative;
}

.service-area-google-map,
.service-area-map-placeholder {
  height: 100%;
  width: 100%;
}

.service-area-map-placeholder {
  background-color: #c9f2df;
  background-image: linear-gradient(
      28deg,
      transparent 47%,
      rgba(82, 166, 139, 0.22) 48%,
      rgba(82, 166, 139, 0.22) 49%,
      transparent 50%
    ),
    linear-gradient(
      152deg,
      transparent 47%,
      rgba(82, 166, 139, 0.18) 48%,
      rgba(82, 166, 139, 0.18) 49%,
      transparent 50%
    );
  background-size: 140px 110px, 190px 145px;
}

.service-area-content h2 {
  color: #3f5968;
  font-size: clamp(1.5rem, 2vw, 2rem);
  line-height: 1.2;
  margin: 0 0 1.35rem;
}

.service-area-content p {
  color: #405a6a;
  font-size: 0.9rem;
  line-height: 1.65;
  margin: 0 0 1.5rem;
}

.service-area-city-list {
  display: grid;
  gap: 0.75rem 2rem;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  list-style: none;
  margin: 0;
  max-height: 250px;
  overflow-y: auto;
  padding: 0 1rem 0 0;
  scrollbar-color: #b7c2c9 transparent;
  scrollbar-width: thin;
}

.service-area-city-list li {
  color: #3f5968;
  font-size: 0.72rem;
  font-weight: 700;
  line-height: 1.35;
  text-transform: uppercase;
}

.service-area-city-list a {
  color: inherit;
  text-decoration: none;
}

.service-area-city-list a:hover {
  color: #0b6b32;
}

.service-area-city-list a:focus-visible {
  border-radius: 2px;
  outline: 2px solid #0b6b32;
  outline-offset: 3px;
}

@media (max-width: 768px) {
  .service-area-module {
    padding: 3rem 1rem;
  }

  .service-area-inner {
    gap: 2rem;
    grid-template-columns: 1fr;
  }

  .service-area-map {
    aspect-ratio: 1.35;
    min-height: 220px;
  }
}
</style>